import { GAME } from '@/config/game';
import { WAVE_LAYOUT } from '@/config/waves';
import type { MinionKind } from '@/entities/minion-defs';

// Integer hash mixing constants (seed × room × salt → 32-bit)
const HASH_ROOM = 2654435761;
const HASH_SALT = 40503;
const HASH_MIX = 2246822519;
const HASH_SHIFT = 15;
/** Salt of the per-room wave-count roll */
const COUNT_SALT = 1;
/** Salt of the per-member elite promotion roll */
const ELITE_SALT = 7;
/** Elite roll resolution (per mille) */
const ELITE_ROLL = 1000;
/** Wave layouts hash with WAVE_SALT + wave index */
export const WAVE_SALT = 100;

/**
 * Pure wave planning (docs/12 C8/M2): counts, squads and formations are a
 * function of seed + room + wave — previewable WITHOUT touching the run rng.
 * 32-bit integer math throughout: a float `h * HASH_MIX` passes 2^53 and
 * zeroes the low bits (every `% n` roll then landed on 0 — fixed in polish 2).
 */
export function roomHash(seed: number, room: number, salt: number): number {
  let h = (seed ^ Math.imul(room, HASH_ROOM) ^ Math.imul(salt, HASH_SALT)) >>> 0;
  h ^= h >>> HASH_SHIFT;
  return Math.imul(h, HASH_MIX) >>> 0;
}

/**
 * Waves before the boss for a room (docs/12 M2). Sector rooms roll inside
 * their plan's [lo, hi]; endless rooms roll 1..ceiling (grows with depth).
 */
export function wavesFor(seed: number, room: number, range: readonly [number, number] | null): number {
  if (range) {
    const [lo, hi] = range;
    return lo + (roomHash(seed, room, COUNT_SALT) % (Math.max(lo, hi) - lo + 1));
  }
  const w = GAME.run.waves;
  if (room < w.minRoom) return 0;
  const ceiling = Math.min(w.maxWaves, 1 + Math.floor((room - w.minRoom) / w.perRooms) + 1);
  return 1 + (roomHash(seed, room, COUNT_SALT) % ceiling);
}

/** Light-squad size of a wave, after the difficulty density multiplier. */
export function squadSize(waveIndex: number, density: number): number {
  const w = GAME.run.waves;
  return Math.round((w.spawnBase + waveIndex * w.spawnPerWave) * density);
}

/** Static bunkers in a wave (docs/12 M8) — 0 before the `from` room gate. */
export function bunkerCount(room: number, waveIndex: number, from: number): number {
  if (room < from) return 0;
  const L = WAVE_LAYOUT;
  return Math.min(L.bunkerMax, 1 + Math.floor((room - from) / L.bunkerRoomStep)
    + Math.floor(waveIndex / L.bunkerWaveStep));
}

/** Preset slot of the i-th bunker in a wave. */
export function bunkerSlot(h: number, i: number): readonly [number, number] {
  const slots = WAVE_LAYOUT.bunkerSlots;
  return slots[(h + i * WAVE_LAYOUT.bunkerSlotStride) % slots.length]!;
}

/**
 * Squad member i's kind — 2-3 kinds per wave keeps each push readable.
 * `kinds` is the room's introduced set (sector schedule, docs/01 D5); a
 * hashed `eliteRatio` share of the members is promoted to elites.
 */
export function squadKind(
  h: number, i: number, kinds: ReadonlyArray<MinionKind>, eliteRatio: number,
): MinionKind {
  const L = WAVE_LAYOUT;
  if (eliteRatio > 0 && roomHash(h, i, ELITE_SALT) % ELITE_ROLL < eliteRatio * ELITE_ROLL) {
    return 'elite';
  }
  return kinds[(h + Math.floor(i / L.squadGroup) * L.squadKindStride) % kinds.length]!;
}

/**
 * Formation anchor (arena fractions) of squad member i, written into `out`.
 * The formation is picked by the wave hash.
 */
export function formationPoint(
  h: number, i: number, count: number, waveIndex: number, out: { x: number; y: number },
): void {
  const F = WAVE_LAYOUT.formations;
  const formation = h % F.count;
  if (formation === 0) {
    out.x = F.column.x; out.y = F.column.rows[waveIndex % F.column.rows.length]!;
  } else if (formation === 1) {
    out.x = F.line.x; out.y = F.line.top + (F.line.span * i) / Math.max(1, count - 1);
  } else if (formation === 2) {
    const even = i % 2 === 0;
    out.x = even ? F.pincer.xEven : F.pincer.xOdd; out.y = even ? F.pincer.yEven : F.pincer.yOdd;
  } else {
    const S = F.scatter;
    // Unsigned shifts: h is a uint32 — `>>` would go negative past 2^31
    out.x = S.x + ((h >>> (i % S.bits)) % S.xSteps) / S.scale;
    out.y = S.y + ((h >>> ((i + S.yShift) % S.bits)) % S.ySteps) / S.scale;
  }
}

/**
 * Advance rooms (docs/01): the room's waves split over its segments — every
 * segment fights at least once, later segments take the extras. @returns the
 * cumulative wave index at the end of each segment (last = room total).
 */
export function segmentWaveEnds(waves: number, segments: number): number[] {
  const total = Math.max(waves, segments);
  const ends: number[] = [];
  for (let i = 0; i < segments; i++) ends.push(Math.floor(((i + 1) * total) / segments));
  return ends;
}
